import { useEffect, useRef } from 'react'
import { EditorView, ViewPlugin, Decoration, keymap, highlightActiveLine } from '@codemirror/view'
import { EditorState, RangeSetBuilder } from '@codemirror/state'
import type { DecorationSet, ViewUpdate } from '@codemirror/view'
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands'
import { markdown } from '@codemirror/lang-markdown'
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language'

// Brand colors from DESIGN.md (CSS vars not available inside CM theme)
const brandAccent = '#5B8DEF'
const neutral100 = 'hsl(240 5% 96%)'
const neutral500 = '#71717A'

const mmindTheme = EditorView.theme(
  {
    '&': {
      height: '100%',
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      lineHeight: '1.7',
    },
    '.cm-content': {
      padding: '16px',
      maxWidth: '720px',
      caretColor: '#1E3A5F',
    },
    '@media (min-width: 761px)': {
      '.cm-content': {
        padding: '16px 24px',
      },
    },
    '.cm-focused': { outline: 'none' },
    '.cm-activeLine': { backgroundColor: neutral100 },
    '.cm-selectionBackground, ::selection': {
      backgroundColor: `${brandAccent}33`,
    },
    // Wikilinks
    '.cm-wikilink': {
      color: brandAccent,
      cursor: 'pointer',
      borderBottom: `1px solid ${brandAccent}55`,
    },
    '.cm-wikilink:hover': {
      borderBottom: `1px solid ${brandAccent}`,
    },
    // YAML frontmatter
    '.cm-frontmatter': {
      color: neutral500,
      fontStyle: 'italic',
    },
    // Inline code
    '.cm-inlineCode, .tok-monospace': {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '13px',
      backgroundColor: neutral100,
      padding: '1px 4px',
      borderRadius: '3px',
    },
    // Code blocks
    '.cm-codeBlock': {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '13px',
      backgroundColor: neutral100,
      display: 'block',
    },
    // Headings
    '.tok-heading1': { fontWeight: '600', fontSize: '1.4em' },
    '.tok-heading2': { fontWeight: '600', fontSize: '1.2em' },
    '.tok-heading3': { fontWeight: '600', fontSize: '1.05em' },
    '.tok-strong': { fontWeight: '600' },
    '.tok-emphasis': { fontStyle: 'italic' },
    '.tok-link, .tok-url': { color: brandAccent },
    '.tok-meta': { color: neutral500 },
    '.cm-line': { wordBreak: 'break-word' },
  },
  { dark: false },
)

// Wikilink decoration plugin
function makeWikilinkPlugin(onNavigateRef: React.MutableRefObject<(path: string) => void>) {
  const wikilinkMark = Decoration.mark({ class: 'cm-wikilink' })

  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet

      constructor(view: EditorView) {
        this.decorations = buildDecorations(view)
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildDecorations(update.view)
        }
      }
    },
    {
      decorations: (v) => v.decorations,
      eventHandlers: {
        click(event: MouseEvent, view: EditorView) {
          const target = event.target as HTMLElement
          if (!target.classList.contains('cm-wikilink')) return
          const pos = view.posAtCoords({ x: event.clientX, y: event.clientY })
          if (pos === null) return
          const text = view.state.doc.toString()
          const re = /\[\[([^\]|]+?)(?:\|[^\]]+)?\]\]/g
          let m
          while ((m = re.exec(text)) !== null) {
            if (pos >= m.index && pos <= m.index + m[0].length && m[1]) {
              onNavigateRef.current(m[1].trim())
              event.preventDefault()
              break
            }
          }
        },
      },
    },
  )

  function buildDecorations(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>()
    const text = view.state.doc.toString()
    const re = /\[\[([^\]]+)\]\]/g
    let m
    while ((m = re.exec(text)) !== null) {
      builder.add(m.index, m.index + m[0].length, wikilinkMark)
    }
    return builder.finish()
  }
}

interface VaultEditorProps {
  content: string
  onChange: (value: string) => void
  onNavigate: (wikilinkTarget: string) => void
  readOnly?: boolean
}

export function VaultEditor({ content, onChange, onNavigate, readOnly = false }: VaultEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const onChangeRef = useRef(onChange)
  const onNavigateRef = useRef(onNavigate)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    onNavigateRef.current = onNavigate
  }, [onNavigate])

  // Initialize editor once
  useEffect(() => {
    if (!containerRef.current) return

    const extensions = [
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
      markdown(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      highlightActiveLine(),
      mmindTheme,
      makeWikilinkPlugin(onNavigateRef),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onChangeRef.current(update.state.doc.toString())
        }
      }),
      EditorView.lineWrapping,
      EditorState.readOnly.of(readOnly),
    ]

    const view = new EditorView({
      state: EditorState.create({ doc: content, extensions }),
      parent: containerRef.current,
    })

    viewRef.current = view
    return () => {
      view.destroy()
      viewRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // init once; content sync handled below

  // Sync external content changes (e.g., different file loaded)
  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const current = view.state.doc.toString()
    if (current !== content) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: content },
        // Preserve cursor if possible
        selection: view.state.selection,
      })
    }
  }, [content])

  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-auto focus-within:outline-none"
      aria-label="Markdown-Editor"
    />
  )
}
